import { Dialog, Text } from "@flat-ui";
import { AdmPlan } from "@custom-types/base-types";
import { formatPlanName } from "@utils/data-display";
import { useAppNavigation } from "@router/use-app-navigation";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { PlanEvents } from "@utils/track-event/track-event-list";
import { canEndCopiedPlan } from "@utils/plan-copy-utils";

interface Props {
  /** The plan that should be copied */
  plan: AdmPlan;

  /** Function called when the user closes the dialog */
  onClose(): void;
}

/**
 * Dialog to start copying a plan. The copy is created on the plan creation page, where its
 * limits and dates can still be adjusted, and where ending the copied plan is offered as soon as
 * the copy would overlap with it.
 */
export function CopyPlanDialog({ plan, onClose }: Props): React.JSX.Element {
  const { navigateToPlanCopy } = useAppNavigation();
  const { trackEvent } = useTrackEvent();

  /** Tells when the copy starts by default, which can still be changed on the next page */
  const startDescription = canEndCopiedPlan(plan)
    ? "The copy will start when this plan ends. If you let it start earlier, you can choose in the next step to deactivate this plan when the copy starts."
    : "Since this plan has already expired, you can select in the next step the start date and duration of the plan";

  function handleConfirm(): void {
    trackEvent({
      name: PlanEvents.requestCopyingPlan,
      props: { planId: plan.id },
    });

    navigateToPlanCopy({ plan });
  }

  return (
    <Dialog
      open={true}
      onCancel={onClose}
      title={`Copy plan "${formatPlanName(plan)}"`}
      onConfirm={handleConfirm}
      confirmText="Next"
    >
      <Text variant="bodyL">
        You can create a copy of this plan with the same product and limits. The
        limits and dates can still be adjusted on the next page.
      </Text>
      <Text variant="bodyL" sx={{ mt: 2 }}>
        {startDescription}
      </Text>
    </Dialog>
  );
}
